import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getApiBaseUrl, publicMediaSrc } from "../config/api";
import DeliveriesSeo from "../components/DeliveriesSeo";
import { deliveryHref } from "../components/deliveryHref";
import useReveal from "../hooks/useReveal";
import deliveriesHero from "../assets/deliveries-hero.jpg";

const PAGE_GUTTER = "px-16 md:px-32 lg:px-40";
const PAGE_SIZE = 9;
const SORTS = new Set(["newest", "oldest", "name", "location"]);

function lockDoubleClick(event) {
  const el = event.currentTarget;
  if (el.dataset.navLock === "1") {
    event.preventDefault();
    return;
  }
  el.dataset.navLock = "1";
  window.setTimeout(() => {
    delete el.dataset.navLock;
  }, 700);
}

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function cleanText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text;
}

function normalizeQuery(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function normalizeLocation(value) {
  return normalizeQuery(value).toLowerCase();
}

function parseSort(value) {
  const raw = String(value || "").trim();
  return SORTS.has(raw) ? raw : "newest";
}

function parsePage(value) {
  const page = Number.parseInt(String(value || "1"), 10);
  if (!Number.isFinite(page) || page < 1) return 1;
  return page;
}

function parseYear(value) {
  const year = Number.parseInt(String(value || ""), 10);
  if (!Number.isFinite(year) || year < 1900 || year > 3000) return "";
  return String(year);
}

function deliveryYear(item) {
  const date = new Date(item?.deliveryDate || 0);
  if (Number.isNaN(date.getTime())) return "";
  return String(date.getFullYear());
}

function idOf(value) {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value).trim();
}

function productNames(item) {
  return (Array.isArray(item?.product) ? item.product : [])
    .map((row) => cleanText(row?.productName))
    .filter(Boolean);
}

function categoryNames(item, nameById = {}) {
  const seen = new Set();
  const names = [];
  for (const row of Array.isArray(item?.product) ? item.product : []) {
    const name = cleanText(row?.category?.categoryName) || cleanText(nameById[idOf(row?.category)]);
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names;
}

function matchesDelivery(item, rawQuery, nameById) {
  const needle = normalizeQuery(rawQuery).toLowerCase();
  if (!needle) return true;
  const fields = [
    item.title,
    item.description,
    item.deliveryLocation,
    productNames(item).join(" "),
    categoryNames(item, nameById).join(" "),
  ];
  return fields.some((field) => String(field || "").toLowerCase().includes(needle));
}

function sortDeliveries(list, sort) {
  const next = [...list];
  const time = (item) => {
    const date = new Date(item?.deliveryDate || 0);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  };
  if (sort === "oldest") return next.sort((a, b) => time(a) - time(b));
  if (sort === "name") {
    return next.sort((a, b) => productNames(a).join(", ").localeCompare(productNames(b).join(", ")));
  }
  if (sort === "location") {
    return next.sort((a, b) => cleanText(a.deliveryLocation).localeCompare(cleanText(b.deliveryLocation)));
  }
  return next.sort((a, b) => time(b) - time(a));
}

function formatDeliveryDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function isoDeliveryDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString();
}

function deliveryImageAlt(item, firstImage) {
  const explicit = cleanText(firstImage?.altText);
  if (explicit) return explicit;
  const materials = productNames(item).join(", ") || "Industrial materials";
  const place = cleanText(item.deliveryLocation) || "a project site in Malaysia";
  return `${materials} delivered to ${place}`;
}

function writeParams({ q, location, year, sort, page }, mode, setSearchParams) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (location) params.set("location", location);
  if (year) params.set("year", year);
  if (sort && sort !== "newest") params.set("sort", sort);
  if (page > 1) params.set("page", String(page));
  setSearchParams(params, { replace: mode === "replace" });
}

function DeliveriesPage() {
  const pageRef = useRef(null);
  const headingRef = useRef(null);
  const shouldScrollToHeading = useRef(false);
  const skipFilterFlash = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [deliveries, setDeliveries] = useState([]);
  const [categoryNameById, setCategoryNameById] = useState({});
  const [contact, setContact] = useState(null);
  const [contactStatus, setContactStatus] = useState("loading");
  const [status, setStatus] = useState("loading");
  const [pageBusy, setPageBusy] = useState(false);
  const [resultsBusy, setResultsBusy] = useState(false);
  const [loadNonce, setLoadNonce] = useState(0);

  useReveal(pageRef);

  const query = searchParams.get("q") || "";
  const requestedLocation = cleanText(searchParams.get("location"));
  const requestedYear = parseYear(searchParams.get("year"));
  const sort = parseSort(searchParams.get("sort"));
  const requestedPage = parsePage(searchParams.get("page"));

  const loadList = useCallback(() => {
    let cancelled = false;
    setStatus("loading");
    setDeliveries([]);
    Promise.all([
      fetch(`${getApiBaseUrl()}/deliveries`, { headers: { Accept: "application/json" } }).then((res) => {
        if (!res.ok) throw new Error("Unable to load deliveries");
        return res.json();
      }),
      fetch(`${getApiBaseUrl()}/categories`, { headers: { Accept: "application/json" } })
        .then((res) => (res.ok ? res.json() : []))
        .catch(() => []),
    ])
      .then(([list, categoryList]) => {
        if (cancelled) return;
        const published = (Array.isArray(list) ? list : []).filter((item) => item?.isPublished !== false);
        const nameById = {};
        (Array.isArray(categoryList) ? categoryList : []).forEach((category) => {
          const id = idOf(category);
          const name = cleanText(category?.categoryName);
          if (id && name) nameById[id] = name;
        });
        setCategoryNameById(nameById);
        setDeliveries(published);
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setDeliveries([]);
        setCategoryNameById({});
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => loadList(), [loadList, loadNonce]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled) return;
        setContact(Array.isArray(list) && list[0] ? list[0] : null);
        setContactStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setContact(null);
        setContactStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nodes = pageRef.current?.querySelectorAll(".hero-in");
      nodes?.forEach((node) => {
        if (getComputedStyle(node).opacity === "0") node.classList.add("is-visible");
      });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, []);

  const locations = useMemo(() => {
    const unique = new Map();
    deliveries.forEach((item) => {
      const label = cleanText(item.deliveryLocation);
      const key = normalizeLocation(label);
      if (!key || unique.has(key)) return;
      unique.set(key, label);
    });
    return [...unique.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
  }, [deliveries]);

  const years = useMemo(() => {
    const unique = new Set();
    deliveries.forEach((item) => {
      const year = deliveryYear(item);
      if (year) unique.add(year);
    });
    return [...unique].sort((a, b) => Number(b) - Number(a));
  }, [deliveries]);

  const locationValue = locations.some((item) => item.value === normalizeLocation(requestedLocation))
    ? normalizeLocation(requestedLocation)
    : "";
  const yearValue = years.includes(requestedYear) ? requestedYear : "";

  const filtered = useMemo(() => {
    const byLocation = locationValue
      ? deliveries.filter((item) => normalizeLocation(item.deliveryLocation) === locationValue)
      : deliveries;
    const byYear = yearValue ? byLocation.filter((item) => deliveryYear(item) === yearValue) : byLocation;
    return sortDeliveries(byYear.filter((item) => matchesDelivery(item, query, categoryNameById)), sort);
  }, [deliveries, query, locationValue, yearValue, sort, categoryNameById]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE) || 1);
  const currentPage = Math.min(requestedPage, pageCount);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const zeroDeliveries = status === "ready" && deliveries.length === 0;
  const noFilterResults = status === "ready" && deliveries.length > 0 && filtered.length === 0;

  useEffect(() => {
    if (status !== "ready") return;
    const locationOk = normalizeLocation(requestedLocation) === locationValue;
    const yearOk = requestedYear === yearValue;
    const pageOk = requestedPage === currentPage;
    const extras = searchParams.get("page") === "1" || searchParams.get("sort") === "newest";
    if (pageOk && locationOk && yearOk && !extras) return;
    writeParams(
      { q: query, location: locationValue, year: yearValue, sort, page: currentPage },
      "replace",
      setSearchParams
    );
  }, [
    status,
    requestedPage,
    currentPage,
    requestedLocation,
    locationValue,
    requestedYear,
    yearValue,
    query,
    sort,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!shouldScrollToHeading.current) return undefined;
    shouldScrollToHeading.current = false;
    const node = headingRef.current;
    if (!node) return undefined;
    node.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    node.focus({ preventScroll: true });
    const timer = window.setTimeout(() => setPageBusy(false), 350);
    return () => window.clearTimeout(timer);
  }, [currentPage]);

  useEffect(() => {
    if (status !== "ready") return undefined;
    if (skipFilterFlash.current) {
      skipFilterFlash.current = false;
      return undefined;
    }
    setResultsBusy(true);
    const timer = window.setTimeout(() => setResultsBusy(false), 180);
    return () => window.clearTimeout(timer);
  }, [query, locationValue, yearValue, sort, status]);

  const setQueryValue = (value) => {
    writeParams({ q: value, location: locationValue, year: yearValue, sort, page: 1 }, "replace", setSearchParams);
  };

  const setLocation = (next) => {
    writeParams({ q: query, location: next, year: yearValue, sort, page: 1 }, "push", setSearchParams);
  };

  const setYear = (next) => {
    writeParams({ q: query, location: locationValue, year: next, sort, page: 1 }, "push", setSearchParams);
  };

  const setSortValue = (next) => {
    writeParams({ q: query, location: locationValue, year: yearValue, sort: parseSort(next), page: 1 }, "push", setSearchParams);
  };

  const goToPage = (next) => {
    if (pageBusy || next === currentPage || next < 1 || next > pageCount) return;
    setPageBusy(true);
    shouldScrollToHeading.current = true;
    writeParams({ q: query, location: locationValue, year: yearValue, sort, page: next }, "push", setSearchParams);
  };

  const clearFilters = () => {
    writeParams({ q: "", location: "", year: "", sort: "newest", page: 1 }, "push", setSearchParams);
  };

  const countLabel =
    status === "loading" ? null : status === "error" ? "Unable to load deliveries" : `${filtered.length} ${filtered.length === 1 ? "delivery" : "deliveries"}`;
  const liveMessage =
    status === "loading"
      ? "Loading deliveries."
      : status === "error"
        ? "Unable to load deliveries."
        : `${filtered.length} ${filtered.length === 1 ? "delivery" : "deliveries"} found.`;
  const showFilters = status === "ready" && !zeroDeliveries;
  const gridBusy = pageBusy || resultsBusy;

  return (
    <div className="deliveries-page" ref={pageRef}>
      <DeliveriesSeo
        contact={contact}
        contactStatus={contactStatus}
        deliveriesReady={status === "ready"}
        items={visible}
        itemCount={filtered.length}
        page={currentPage}
        pageSize={PAGE_SIZE}
      />
      <p className="sr-only" aria-live="polite">
        {liveMessage}
      </p>
      <section className="relative overflow-hidden bg-at-dark text-white">
        <img
          src={deliveriesHero}
          alt=""
          width={1024}
          height={341}
          fetchPriority="high"
          className="hero-zoom-subtle pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="pointer-events-none absolute inset-0 bg-black/20" />
        <div className="pointer-events-none absolute inset-y-0 left-0 hidden h-full w-[58%] bg-gradient-to-r from-black/75 via-black/40 to-transparent md:block" />
        <div className={`relative z-10 flex min-h-[320px] flex-col justify-between py-10 md:min-h-[420px] md:py-12 ${PAGE_GUTTER}`}>
          <nav aria-label="Breadcrumb" className="hero-in text-xs text-white/70">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span className="px-2 text-white/40">/</span>
            <span className="text-white">Deliveries</span>
          </nav>
          <div className="mb-8 max-w-xl md:mb-14">
            <p className="hero-in hero-in-1 mt-8 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
              <span className="inline-block h-1 w-12 bg-at-gold" aria-hidden="true" />
              Supply in Motion
            </p>
            <h1 className="hero-in hero-in-2 mt-2 font-serif text-3xl uppercase leading-tight md:text-5xl">
              Our Deliveries
            </h1>
            <p className="hero-in hero-in-3 mt-3 max-w-md text-sm leading-relaxed text-white/70">
              See materials and industrial products supplied to project sites across Malaysia.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className={PAGE_GUTTER}>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2
                ref={headingRef}
                tabIndex={-1}
                className="scroll-mt-24 font-serif text-3xl uppercase text-at-dark outline-none md:text-4xl"
              >
                Recent Deliveries
              </h2>
              <p className="mt-2 text-sm text-neutral-500">
                {status === "loading" ? (
                  <span className="inline-block h-4 w-24 bg-neutral-200 align-middle" aria-hidden="true" />
                ) : (
                  countLabel
                )}
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center lg:w-auto">
              {status === "loading" ? (
                <>
                  <span className="block h-[42px] w-full rounded-full bg-neutral-200 sm:w-64" aria-hidden="true" />
                  <span className="block h-[42px] w-full rounded-full bg-neutral-200 sm:w-48" aria-hidden="true" />
                  <span className="block h-[42px] w-full rounded-full bg-neutral-200 sm:w-48" aria-hidden="true" />
                </>
              ) : showFilters ? (
                <>
                  <label className="relative block w-full sm:w-64">
                    <span className="sr-only">Search deliveries</span>
                    <svg
                      viewBox="0 0 24 24"
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <circle cx="11" cy="11" r="7" />
                      <path d="M20 20l-3-3" />
                    </svg>
                    <input
                      type="search"
                      value={query}
                      onChange={(event) => setQueryValue(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape" && query) {
                          event.preventDefault();
                          setQueryValue("");
                        }
                      }}
                      placeholder="Search deliveries"
                      className={`category-search w-full rounded-full border border-neutral-200 bg-white py-2.5 pl-10 text-sm text-at-dark outline-none placeholder:text-neutral-400 focus:border-at-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold ${query ? "pr-10" : "pr-4"}`}
                    />
                    {query ? (
                      <button
                        type="button"
                        aria-label="Clear search"
                        onClick={() => setQueryValue("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-at-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
                      >
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M6 6l12 12M18 6L6 18" />
                        </svg>
                      </button>
                    ) : null}
                  </label>
                  <label className="relative block w-full sm:w-48">
                    <span className="sr-only">Filter by location</span>
                    <select
                      value={locationValue}
                      disabled={locations.length === 0}
                      onChange={(event) => setLocation(event.target.value)}
                      className="w-full appearance-none rounded-full border border-neutral-200 bg-white py-2.5 pl-4 pr-10 text-sm text-at-dark outline-none focus:border-at-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">All Locations</option>
                      {locations.map((location) => (
                        <option key={location.value} value={location.value}>
                          {location.label}
                        </option>
                      ))}
                    </select>
                    <svg
                      viewBox="0 0 24 24"
                      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </label>
                  <label className="relative block w-full sm:w-48">
                    <span className="sr-only">Sort deliveries</span>
                    <select
                      value={sort}
                      disabled={status !== "ready"}
                      onChange={(event) => setSortValue(event.target.value)}
                      className="w-full appearance-none rounded-full border border-neutral-200 bg-white py-2.5 pl-4 pr-10 text-sm text-at-dark outline-none focus:border-at-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="newest">Sort by: Newest</option>
                      <option value="oldest">Sort by: Oldest</option>
                      <option value="name">Sort by: Product Name A–Z</option>
                      <option value="location">Sort by: Location A–Z</option>
                    </select>
                    <svg
                      viewBox="0 0 24 24"
                      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </label>
                </>
              ) : null}
            </div>
          </div>

          {showFilters && years.length ? (
            <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Filter by year">
              <button
                type="button"
                onClick={() => setYear("")}
                aria-pressed={!yearValue}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold ${yearValue ? "border border-neutral-200 text-at-dark" : "bg-at-dark text-white"}`}
              >
                All Years
              </button>
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  onClick={() => setYear(year)}
                  aria-pressed={yearValue === year}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold ${yearValue === year ? "bg-at-dark text-white" : "border border-neutral-200 text-at-dark"}`}
                >
                  {year}
                </button>
              ))}
            </div>
          ) : null}

          <div className="mt-8" aria-busy={status === "loading" || gridBusy}>
            {status === "loading" ? (
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3" aria-hidden="true">
                {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                  <div key={index}>
                    <div className="aspect-[4/3] bg-neutral-200" />
                    <div className="mt-4 h-4 w-3/4 bg-neutral-200" />
                    <div className="mt-2 h-3 w-1/2 bg-neutral-200" />
                    <div className="mt-2 h-3 w-2/3 bg-neutral-200" />
                  </div>
                ))}
              </div>
            ) : null}

            {status === "error" ? (
              <div className="text-sm text-neutral-500">
                <p>Unable to load deliveries.</p>
                <button
                  type="button"
                  onClick={(event) => {
                    lockDoubleClick(event);
                    setLoadNonce((value) => value + 1);
                  }}
                  className="hero-btn mt-3 font-semibold text-at-dark underline decoration-at-gold underline-offset-4 hover:text-at-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
                >
                  Retry
                </button>
              </div>
            ) : null}

            {zeroDeliveries ? (
              <div className="max-w-xl">
                <h3 className="font-serif text-2xl uppercase text-at-dark">No deliveries available yet</h3>
                <p className="mt-3 text-sm text-neutral-500">
                  New delivery updates will be added here. Browse our products or contact us about your requirements.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/products"
                    onClick={lockDoubleClick}
                    className="hero-btn inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
                  >
                    Browse Products <span className="btn-arrow">→</span>
                  </Link>
                  <Link
                    to="/contact?intent=quote#contact-form"
                    onClick={lockDoubleClick}
                    className="hero-btn inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-at-dark hover:border-at-gold"
                  >
                    Request a Quote <span className="btn-arrow">→</span>
                  </Link>
                </div>
              </div>
            ) : null}

            {noFilterResults ? (
              <div className="max-w-xl">
                <h3 className="font-serif text-2xl uppercase text-at-dark">No matching deliveries</h3>
                <p className="mt-3 text-sm text-neutral-500">Try changing your search term, location or year filter.</p>
                <button
                  type="button"
                  onClick={(event) => {
                    lockDoubleClick(event);
                    clearFilters();
                  }}
                  className="hero-btn mt-6 inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
                >
                  Clear Filters <span className="btn-arrow">→</span>
                </button>
              </div>
            ) : null}

            {status === "ready" && visible.length ? (
              <ul className={`product-results grid gap-8 sm:grid-cols-2 lg:grid-cols-3 ${gridBusy ? "is-updating" : ""}`}>
                {visible.map((item) => {
                  const firstImage = Array.isArray(item.images) ? item.images[0] : null;
                  const photo = publicMediaSrc(firstImage?.url);
                  const names = categoryNames(item, categoryNameById).join(", ");
                  const location = cleanText(item.deliveryLocation);
                  const dateLabel = formatDeliveryDate(item.deliveryDate);
                  const dateTime = isoDeliveryDate(item.deliveryDate);
                  return (
                    <li key={item._id || item.slug}>
                      <Link to={deliveryHref(item)} className="delivery-card block">
                        <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                          {photo ? (
                            <img
                              src={photo}
                              alt={deliveryImageAlt(item, firstImage)}
                              width={800}
                              height={600}
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          ) : null}
                        </div>
                        <h3 className="mt-4 text-sm font-semibold text-at-dark">{cleanText(item.title)}</h3>
                        {names ? <p className="mt-1 text-xs text-neutral-500">{names}</p> : null}
                        <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500">
                          {location ? (
                            <span className="inline-flex items-center gap-1">
                              <svg viewBox="0 0 24 24" className="delivery-meta-icon h-3.5 w-3.5 text-at-gold" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                                <path d="M12 21s7-5.3 7-11a7 7 0 10-14 0c0 5.7 7 11 7 11z" />
                                <circle cx="12" cy="10" r="2" />
                              </svg>
                              {location}
                            </span>
                          ) : null}
                          {dateLabel ? (
                            <span className="inline-flex items-center gap-1">
                              <svg viewBox="0 0 24 24" className="delivery-meta-icon h-3.5 w-3.5 text-at-gold" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                                <rect x="4" y="5" width="16" height="15" rx="1.5" />
                                <path d="M8 3v4M16 3v4M4 10h16" />
                              </svg>
                              {dateTime ? <time dateTime={dateTime}>{dateLabel}</time> : dateLabel}
                            </span>
                          ) : null}
                        </p>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>

          {status === "ready" && filtered.length > PAGE_SIZE ? (
            <nav aria-label="Pagination" className="mt-10 flex items-center justify-center gap-3 text-sm text-neutral-500">
              <button
                type="button"
                disabled={pageBusy || currentPage <= 1}
                onClick={() => goToPage(currentPage - 1)}
                className="disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
              >
                Previous
              </button>
              {Array.from({ length: pageCount }).map((_, index) => {
                const number = index + 1;
                const active = number === currentPage;
                return (
                  <button
                    key={number}
                    type="button"
                    disabled={pageBusy}
                    aria-current={active ? "page" : undefined}
                    onClick={() => goToPage(number)}
                    className={`flex h-8 w-8 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold ${active ? "bg-at-gold text-at-dark" : "hover:text-at-dark"}`}
                  >
                    {number}
                  </button>
                );
              })}
              <button
                type="button"
                disabled={pageBusy || currentPage >= pageCount}
                onClick={() => goToPage(currentPage + 1)}
                className="disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
              >
                Next
              </button>
            </nav>
          ) : null}
        </div>
      </section>

      <section className="bg-neutral-100">
        <ul className={`flex flex-col gap-6 py-8 md:flex-row md:items-center md:justify-between md:gap-0 ${PAGE_GUTTER}`}>
          <li className="trust-item flex items-center gap-4" data-reveal="">
            <svg viewBox="0 0 24 24" className="h-8 w-8 shrink-0 text-at-dark" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <rect x="8" y="3.5" width="8" height="3" rx="0.8" />
              <rect x="5" y="5.5" width="14" height="15" rx="1.5" />
              <path d="M8 11h8M8 14.5h6" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-at-dark">Project-Specific Sourcing</p>
              <p className="mt-0.5 text-xs text-neutral-500">Materials matched to your project needs.</p>
            </div>
          </li>
          <li className="hidden px-6 text-lg text-neutral-300 md:block" aria-hidden="true">
            |
          </li>
          <li className="trust-item flex items-center gap-4" data-reveal="" data-reveal-delay="1">
            <svg viewBox="0 0 24 24" className="h-8 w-8 shrink-0 text-at-dark" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M3 7h11v8H3z" />
              <path d="M14 10h4l3 3v2h-7" />
              <circle cx="7" cy="17" r="1.5" />
              <circle cx="18" cy="17" r="1.5" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-at-dark">Delivery Coordination</p>
              <p className="mt-0.5 text-xs text-neutral-500">On-time delivery to your site.</p>
            </div>
          </li>
          <li className="hidden px-6 text-lg text-neutral-300 md:block" aria-hidden="true">
            |
          </li>
          <li className="trust-item flex items-center gap-4" data-reveal="" data-reveal-delay="2">
            <svg viewBox="0 0 24 24" className="h-8 w-8 shrink-0 text-at-dark" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M12 21s7-5.4 7-11a7 7 0 10-14 0c0 5.6 7 11 7 11z" />
              <circle cx="12" cy="10" r="2.2" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-at-dark">Supply Across Malaysia</p>
              <p className="mt-0.5 text-xs text-neutral-500">From major cities to regional sites.</p>
            </div>
          </li>
        </ul>
      </section>
    </div>
  );
}

export default DeliveriesPage;
